import React, { useCallback } from 'react';
import { assertUnreachable } from 'utils';
import { useAppSelector } from 'store/hooks';
import { useFocusEffect } from 'hooks';
import { clearOnboardingHistory } from 'features/wealth/utils/onboardingHistory';
import { selectPrimaryAddressForGB } from 'reducers/selectors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { AddressPostCodeScreen } from 'features/settings';
import { OnboardingFlow } from 'utils/types/navigation';
import { selectPotsStatuses } from 'features/pots/reducers/selectors';
import { SelectFundingAccountScreen } from 'features/pots';
import { selectHasNetworth, selectFundingSource, selectHasAnnualIncome, selectEmploymentStatus, selectShouldAskForExtraInsuranceNumber, selectShowMultiAccountTradingView, selectHideFundingAccountAndAutoinvestFromInvestOnboarding, } from '../reducers/selectors';
import AddressScreen from '../screens/AddressScreen';
import NetWorthScreen from '../screens/NetWorthScreen';
import InvestInitialScreen from './InvestInitialScreen';
import { useConnectedAccount, useOnboardingState } from '../hooks/selectors';
import InsuranceScreen from '../screens/InsuranceScreen';
import AnnualIncomeScreen from '../screens/AnnualIncomeScreen';
import FundingSourceScreen from '../screens/FundingSourceScreen';
import { getNextScreen } from '../hooks/useNavigateToNextScreen';
import EmploymentStatusScreen from '../screens/EmploymentStatusScreen';
import BrokerRequirementsScreen from '../screens/BrokerRequirementsScreen';
import StartInvestReadyMadeFundsScreen from '../screens/StartInvestReadyMadeFundsScreen';
import AutoinvestSelectFundingAccountScreen from '../screens/AutoinvestSelectFundingAccountScreen';
import InvestReviewAndConfirmScreen from '../screens/InvestReviewAndConfirmScreen';
import JISAExistingAccountCheck from '../screens/JISAExistingAccountCheck';
import ISAExistingAccountCheck from '../screens/ISAExistingAccountCheck';
import JISAChildPersonalDetails from '../screens/JISAChildPersonalDetails';
import JISAChildAddress from '../screens/JISAChildAddress';
import PersonalDetailsScreen from '../screens/PersonalDetailsScreen';
import SetupContributionsChooseAmountScreen from '../screens/SetupContributionsChooseAmountScreen';
import AutoinvestIntroScreen from '../screens/AutoinvestIntroScreen';
const routeParams = {
    params: {
        flow: OnboardingFlow.Invest,
        mountedOnAnotherScreen: true,
    },
};
const DisplayNextOnboardingStep = () => {
    const navigation = useMainStackNavigation();
    // Landing on the tab resumes onboarding at the latest step, so forget the steps seen before.
    useFocusEffect(useCallback(() => {
        clearOnboardingHistory('invest');
    }, []));
    const onboardingState = useOnboardingState();
    const hasNetWorth = useAppSelector(selectHasNetworth);
    const fundingSource = useAppSelector(selectFundingSource);
    const hasAnnualIncome = useAppSelector(selectHasAnnualIncome);
    const employmentStatus = useAppSelector(selectEmploymentStatus);
    const primaryAddress = useAppSelector(selectPrimaryAddressForGB);
    const userTaxId = useAppSelector((state) => state.user.user.taxId);
    const { status: potsFeatureStatus } = useAppSelector(selectPotsStatuses);
    const isMigrationFlow = useAppSelector(selectShowMultiAccountTradingView);
    const hasStartedKYC = useAppSelector((state) => !!state.user.user.kycStartedAt);
    const shouldAskForExtraInsuranceNumber = useAppSelector(selectShouldAskForExtraInsuranceNumber);
    const hasOnboardedPots = useAppSelector((state) => state.pots.account?.status ? state.pots.account?.status !== 'NOT_STARTED' : false);
    const hasTriedSettingsUpInvest = useAppSelector((state) => state.deviceSettings.hasTriedSettingsUpInvest);
    const connectedAccount = useConnectedAccount();
    const hideFundingAccountAndAutoinvestFlow = useAppSelector(selectHideFundingAccountAndAutoinvestFromInvestOnboarding);
    const nav = navigation;
    if (!hasTriedSettingsUpInvest) {
        return <InvestInitialScreen />;
    }
    const screenToShow = getNextScreen(onboardingState, !!primaryAddress, !!primaryAddress?.kycStatus, userTaxId, shouldAskForExtraInsuranceNumber, hasStartedKYC, hasOnboardedPots, hasAnnualIncome, hasNetWorth, !!fundingSource, !!employmentStatus, connectedAccount, isMigrationFlow, hideFundingAccountAndAutoinvestFlow);
    switch (screenToShow.name) {
        case 'InvestSelectSecclAccountType': {
            return <InvestInitialScreen />;
        }
        case 'InvestPersonalDetails': {
            return <PersonalDetailsScreen navigation={nav} route={routeParams}/>;
        }
        case 'AddressPostCodeScreen': {
            return <AddressPostCodeScreen navigation={nav} route={routeParams}/>;
        }
        case 'InvestAddress': {
            if ((potsFeatureStatus !== 'NOT_STARTED' && potsFeatureStatus !== undefined) || primaryAddress?.kycStatus) {
                return <InsuranceScreen navigation={nav} route={routeParams}/>;
            }
            if (primaryAddress) {
                return <AddressScreen navigation={nav} route={routeParams}/>;
            }
            return <AddressPostCodeScreen navigation={nav} route={routeParams}/>;
        }
        case 'InvestInsurance': {
            return <InsuranceScreen navigation={nav} route={routeParams}/>;
        }
        case 'InvestAnnualIncome': {
            return <AnnualIncomeScreen navigation={nav} route={routeParams}/>;
        }
        case 'InvestNetWorth': {
            return <NetWorthScreen navigation={nav} route={routeParams}/>;
        }
        case 'InvestFundingSource': {
            return <FundingSourceScreen navigation={nav} route={routeParams}/>;
        }
        case 'InvestEmploymentStatus': {
            return <EmploymentStatusScreen navigation={nav} route={routeParams}/>;
        }
        case 'InvestBrokerRequirements': {
            return (<BrokerRequirementsScreen navigation={nav} route={{ params: { ...screenToShow.params, ...routeParams.params } }}/>);
        }
        case 'StartInvestReadyMadeFunds': {
            return <StartInvestReadyMadeFundsScreen navigation={nav} route={routeParams}/>;
        }
        case 'SetupContributionsChooseInitialAmount':
        case 'SetupContributionsChooseMonthlyAmount': {
            return (<SetupContributionsChooseAmountScreen navigation={nav} route={{ params: { ...screenToShow.params, ...routeParams.params } }}/>);
        }
        case 'AutoinvestIntro': {
            return (<AutoinvestIntroScreen navigation={nav} route={{ params: { ...screenToShow.params, ...routeParams.params } }}/>);
        }
        case 'SelectFundingAccount': {
            return <SelectFundingAccountScreen navigation={nav} route={routeParams}/>;
        }
        case 'AutoinvestSelectFundingAccount': {
            return (<AutoinvestSelectFundingAccountScreen navigation={nav} route={{ params: { ...screenToShow.params, ...routeParams.params } }}/>);
        }
        case 'JISAExistingAccountCheck': {
            return <JISAExistingAccountCheck navigation={nav} route={routeParams}/>;
        }
        case 'ISAExistingAccountCheck': {
            return <ISAExistingAccountCheck navigation={nav} route={routeParams}/>;
        }
        case 'JISAChildPersonalDetails': {
            return (<JISAChildPersonalDetails navigation={nav} route={{ params: { ...screenToShow.params, ...routeParams.params } }}/>);
        }
        case 'JISAChildAddress': {
            return (<JISAChildAddress navigation={nav} route={{ params: { ...screenToShow.params, ...routeParams.params } }}/>);
        }
        case 'InvestReviewAndConfirm': {
            return <InvestReviewAndConfirmScreen navigation={nav} route={routeParams}/>;
        }
        default: {
            assertUnreachable(screenToShow);
            return <InvestInitialScreen />;
        }
    }
};
export default React.memo(DisplayNextOnboardingStep);
